import { PlusOutlined } from '@ant-design/icons';
import { type CSSProperties, useMemo } from 'react';
import styled from 'styled-components';

import { DropdownBackground, Flex, Space } from '@/components/base';
import PropertyValue from '@/components/Typography/PropertyValue';
import { ADD_TAG_LABEL, NOT_TAGGED_LABEL } from '@/configs/reserved-word';
import Label from '@/features/Feedback/field/Tag/Label';
import useColor from '@/hooks/domain/useColor';
import useUserRole from '@/hooks/domain/useUserRole';
import useKeymap from '@/hooks/pure/useKeymap';
import useDropdownStore from '@/stores/pure/DropdownStore';
import useTagStore from '@/stores/TagStore';
import feedbackUtils from '@/utils/feedback';

import AgentTagSelectModal from '../AgentTagSelectModal';

interface Props {
  selectedTags?: string[];
  onConfirm: (tags: string[]) => void;
  listStyle?: CSSProperties;
  suggestions?: string[];
  name: string;
  isCreate?: boolean;
}

const AgentTagSelector = (props: Props) => {
  const { isEditor } = useUserRole();
  const { currDropdown, setCurrDropdown, shortcutEnabled } = useDropdownStore(
    ({ currDropdown, setCurrDropdown, shortcutEnabled }) => ({
      currDropdown,
      setCurrDropdown,
      shortcutEnabled,
    }),
  );
  const { selectedTags } = props;
  const color = useColor();
  const { tagMap } = useTagStore((state) => ({
    tagMap: state.tagMap,
  }));

  const toggleTagSelectModal = () => {
    isEditor && setCurrDropdown(props.name);
  };

  const tagDropdownOpen = useMemo(() => {
    return currDropdown === props.name;
  }, [currDropdown, props.name]);

  useKeymap('KeyT', () => {
    shortcutEnabled && toggleTagSelectModal();
  });

  return (
    <Style>
      <Flex
        d="row"
        g="8px"
        style={{ flexWrap: 'wrap', maxWidth: '400px', ...props.listStyle }}
      >
        {selectedTags !== undefined && selectedTags.length > 0 ? (
          selectedTags
            .filter((key) => tagMap[key])
            .map((key: string) => {
              const tag = tagMap[key];
              const tagHTML = feedbackUtils.getTagHTML(tagMap, key);

              return (
                <Label
                  key={tag.alias}
                  themeColor={tag.color}
                  text={
                    <span
                      dangerouslySetInnerHTML={{
                        __html: tagHTML,
                      }}
                    ></span>
                  }
                  isSelected={false}
                  onClick={toggleTagSelectModal}
                  style={{ borderColor: color.gray100 }}
                />
              );
            })
        ) : (
          <PropertyValue
            value={NOT_TAGGED_LABEL}
            onClick={toggleTagSelectModal}
            style={{ borderRadius: '16px' }}
          />
        )}
        {/* Add '+ Add tag' label at the end */}
        <PropertyValue
          icon={
            <PlusOutlined style={{ color: color.gray600, fontSize: '12px' }} />
          }
          value={ADD_TAG_LABEL}
          onClick={toggleTagSelectModal}
          style={{ borderRadius: '16px' }}
        />
      </Flex>
      {isEditor && (
        <>
          {selectedTags && selectedTags.length > 0 && <Space h="8px" />}
          {tagDropdownOpen && (
            <>
              <DropdownBackground onClick={() => setCurrDropdown(null)} />
              <AgentTagSelectModal
                {...props}
                style={{
                  width: '400px',
                  position: 'absolute',
                  top: -12,
                  left: 0,
                }}
                closeModal={() => setCurrDropdown(null)}
              />
            </>
          )}
          <Space h="8px" />{' '}
        </>
      )}
    </Style>
  );
};

const Style = styled(Flex).attrs({
  d: 'column',
})`
  position: relative;
`;

export default AgentTagSelector;
